import { CheckCircle2 } from "lucide-react";
import { RevealSection } from "@/components/common/RevealSection";
import { MediaChannelLinks } from "@/components/common/MediaChannelLinks";
import { globalLearnerRegions, teacherImage, siteConfig } from "@/content/siteData";

const teachingPoints = [
  "Nearly 30 years of teaching experience across classroom and online formats",
  "Served as Hindi Trainer for the Embassy of Denmark",
  "Dedicated children learning track covering spoken Hindi, reading, writing, and comprehension",
  "Innovative instruction using storytelling, flashcards, interactive games, and Hindi stories for children",
  "Technology-enabled modules with videos, podcasts, and learning apps for engaging sessions",
  "Practical spoken Hindi through real-life scenarios like shopping, ordering food, and travel",
  "Personalized feedback and regular assessments to support steady progress",
  "Adult-focused courses tailored to spoken Hindi goals for confident daily communication",
  "Currently mentoring 30+ expat learners at any given time",
];

export default function AboutPage() {
  return (
    <div className="px-6 md:px-12 lg:px-24" data-testid="about-page">
      <RevealSection className="mx-auto grid w-full max-w-7xl gap-10 py-14 md:grid-cols-[0.95fr_1.05fr] md:py-20">
        <div className="relative mx-auto w-full max-w-[460px] overflow-hidden rounded-2xl border border-border bg-white p-3 soft-card md:mx-0 md:max-w-[420px] md:aspect-[3/4]" data-testid="about-teacher-image-wrapper">
          <img
            src={teacherImage}
            alt="Hindi teacher smiling during online class"
            className="h-full w-full rounded-xl border-2 border-primary/20 object-cover object-top"
            data-testid="about-teacher-image"
          />
        </div>

        <div className="space-y-5">
          <p className="text-base text-primary md:text-lg" data-testid="about-label">
            Meet your teacher
          </p>
          <h1 className="text-4xl font-bold leading-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="about-heading">
            Hi, I’m {siteConfig.teacherName}. I help expats feel at home through Hindi.
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground md:text-base" data-testid="about-description">
            Hello! Namaskar! I’m a seasoned Hindi educator based in New Delhi, working with global learners including
            diplomats, corporate expats, spouses, and school students. My sessions combine language, culture, and
            real-world confidence so learners can communicate naturally in everyday India.
          </p>
          <p className="rounded-xl border border-border bg-muted/35 p-4 text-sm italic text-foreground/90" data-testid="about-signature-quote">
            “Language is the key to truly living in India — not just visiting it.”
          </p>

          <MediaChannelLinks
            testIdPrefix="about-media-channels"
            heading="Watch & listen"
            className="border-primary/20 bg-primary/5"
          />

          <div className="grid gap-3">
            {teachingPoints.map((point) => (
              <div
                key={point}
                className="flex items-start gap-3 rounded-xl border border-border bg-white p-4"
                data-testid={`about-point-${point.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-secondary" />
                <p className="text-sm text-foreground/90 md:text-base">{point}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border bg-white p-4" data-testid="about-global-regions-section">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary/80" data-testid="about-global-regions-heading">
              Learners across
            </p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="about-global-regions-list">
              {globalLearnerRegions.map((region) => (
                <span
                  key={region}
                  className="rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-foreground/90"
                  data-testid={`about-region-${region.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {region}
                </span>
              ))}
            </div>
          </div>

        </div>
      </RevealSection>
    </div>
  );
}